import React, { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { useTheme, useReveal } from "@/hooks/useTheme";
import { useAnalytics } from "@/hooks/useAnalytics";
import { Nav } from "@/components/Nav";
import { Footer } from "@/components/Footer";

import Home from "@/pages/Home";
import About from "@/pages/About";
import WhatWeDo from "@/pages/WhatWeDo";
import PlatformsIndex from "@/pages/PlatformsIndex";
import PlatformDetail from "@/pages/PlatformDetail";
import PlatformGrantha, { PlatformGranthaModule } from "@/pages/PlatformGrantha";
import PlatformVidhana from "@/pages/PlatformVidhana";
import PlatformDrishti from "@/pages/PlatformDrishti";
import PlatformSamgraha from "@/pages/PlatformSamgraha";
import PlatformSutradhar from "@/pages/PlatformSutradhar";
import PlatformNirnaya from "@/pages/PlatformNirnaya";
import PlatformRachana from "@/pages/PlatformRachana";
import PlatformPariksa from "@/pages/PlatformPariksa";
import IndustriesPage from "@/pages/IndustriesPage";
import CaseDetail from "@/pages/CaseDetail";
import Resources from "@/pages/Resources";
import ResourceDetail from "@/pages/ResourceDetail";
import Contact from "@/pages/Contact";
import AdminLeads from "@/pages/AdminLeads";
import Privacy from "@/pages/Privacy";
import Cookies from "@/pages/Cookies";
import Trust from "@/pages/Trust";
import CookieConsent from "@/components/CookieConsent";

/**
 * Static asset redirect — production CDN falls back to SPA index.html
 * for directory URLs (/logo-kit/). Replacing the location with the
 * explicit file path ensures the static index is served directly.
 */
function LogoKitRedirect() {
  useEffect(() => {
    window.location.replace("/logo-kit/index.html");
  }, []);
  return (
    <div style={{ padding: 40, fontFamily: "system-ui, sans-serif" }}>
      Loading Capexara logo kit&hellip;{" "}
      <a href="/logo-kit/index.html">Open it now</a>.
    </div>
  );
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [pathname]);
  return null;
}

function Shell() {
  const [theme, setTheme] = useTheme();
  useReveal();
  useAnalytics();
  return (
    <>
      <Nav theme={theme} setTheme={setTheme} />
      <ScrollToTop />
      <main>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/what-we-do" element={<WhatWeDo />} />
          <Route path="/about" element={<About />} />
          <Route path="/platforms" element={<PlatformsIndex />} />
          {/* English (canonical) routes */}
          <Route path="/platforms/docuweave" element={<PlatformGrantha />} />
          <Route path="/platforms/docuweave/modules/:slug" element={<PlatformGranthaModule />} />
          <Route path="/platforms/luminex" element={<PlatformVidhana />} />
          <Route path="/platforms/panorix" element={<PlatformDrishti />} />
          <Route path="/platforms/procurex" element={<PlatformSamgraha />} />
          <Route path="/platforms/cognara" element={<PlatformSutradhar />} />
          <Route path="/platforms/evalore" element={<PlatformNirnaya />} />
          <Route path="/platforms/formigen" element={<PlatformRachana />} />
          <Route path="/platforms/assayo" element={<PlatformPariksa />} />
          {/* Legacy Sanskrit slugs — 301-style redirect to English canonical */}
          <Route path="/platforms/grantha" element={<Navigate to="/platforms/docuweave" replace />} />
          <Route path="/platforms/grantha/modules/:slug" element={<Navigate to="/platforms/docuweave" replace />} />
          <Route path="/platforms/vidhana" element={<Navigate to="/platforms/luminex" replace />} />
          <Route path="/platforms/drishti" element={<Navigate to="/platforms/panorix" replace />} />
          <Route path="/platforms/samgraha" element={<Navigate to="/platforms/procurex" replace />} />
          <Route path="/platforms/sutradhar" element={<Navigate to="/platforms/cognara" replace />} />
          <Route path="/platforms/nirnaya" element={<Navigate to="/platforms/evalore" replace />} />
          <Route path="/platforms/rachana" element={<Navigate to="/platforms/formigen" replace />} />
          <Route path="/platforms/pariksa" element={<Navigate to="/platforms/assayo" replace />} />
          <Route path="/platforms/pariksha" element={<Navigate to="/platforms/assayo" replace />} />
          <Route path="/platforms/:slug" element={<PlatformDetail />} />
          <Route path="/industries" element={<IndustriesPage />} />
          <Route path="/cases/:slug" element={<CaseDetail />} />
          <Route path="/resources" element={<Resources />} />
          <Route path="/resources/:id" element={<ResourceDetail />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/admin" element={<AdminLeads />} />
          <Route path="/admin/leads" element={<AdminLeads />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/cookies" element={<Cookies />} />
          <Route path="/trust" element={<Trust />} />
          <Route path="/logo-kit" element={<LogoKitRedirect />} />
          <Route path="/logo-kit/" element={<LogoKitRedirect />} />
          <Route path="*" element={<Home />} />
        </Routes>
      </main>
      <Footer />
      <CookieConsent />
    </>
  );
}

function App() {
  return (
    <div className="App">
      <Toaster position="top-right" richColors />
      <BrowserRouter>
        <Shell />
      </BrowserRouter>
    </div>
  );
}

export default App;
